import React, { Fragment, useEffect, useState } from 'react'
import _debounce from 'lodash/debounce'
import { validatePak } from 'lib/util'
import { useSelector, useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { RouteComponentProps } from 'react-router-dom'
import userOperations from 'store/css-admin/userManager/operations'
import * as modalActionsCreators from 'store/modal'
import lmsOperations from 'store/css-admin/lmsManager/operations'
import { searchNetSuiteAssets } from 'store/netSuiteAssetManager/operations'
import { RootState } from 'types'
import moment from 'moment'
import {
  Alert,
  Row,
  Col,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap'
import AutoTable from 'components/AutoTable'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import UserProfile from './UserProfile'
import SitesGrid from './SitesGrid/SitesGrid'
import { NetSuiteAsset } from 'types/netsuite'

const Tabs = {
  Sites: 1,
  Paks: 2,
  TrainingStatus: 3,
}

interface Pak {
  pakNumber: string
  grade: string
  registrationDate: string
  [key: string]: any
}

interface TrainingReport {
  type: string
  skuCode: string
  current_track_title?: string
  status?: string
  doneAt?: string | null
  [key: string]: any
}

interface UserRouteParams {
  email: string
  id: string
}

interface UserProps extends RouteComponentProps<UserRouteParams> {}

export const User = ({ match }: UserProps) => {
  const dispatch = useDispatch()

  const {
    cssLmsTrainingReport = [],
    cssLmsTrainingTrack = [],
    deleteUserResults,
    isDeletingUser,
    isLoading,
    isSaving,
    isSavingTraining,
    productUser,
    progressReport,
  } = useSelector((state: RootState) => {
    const { userManager, lmsManager, deleteUserManager } = state
    return {
      cssLmsTrainingReport: lmsManager.training_report || [],
      cssLmsTrainingTrack: lmsManager.training_track || [],
      isSavingTraining: lmsManager.isSavingTraining,
      isDeletingUser: deleteUserManager.isDeletingUser,
      deleteUserResults: deleteUserManager.deleteUserResults,
      isLoading: userManager.isLoading,
      isSaving: userManager.isSaving,
      productUser: userManager.productUser,
      progressReport: userManager.progressReport || { isLoading: false },
    }
  })

  const netSuiteAssets = useSelector((state: RootState) => {
    const { netSuiteAssetManager } = state
    return {
      isLoading: netSuiteAssetManager.isLoading,
      assets: Object.values(
        netSuiteAssetManager.netSuiteAssets,
      ) as NetSuiteAsset[],
      error: netSuiteAssetManager.error,
    }
  })

  const addPakToProductUser = (pakNo: string) =>
    dispatch(userOperations.addPakToProductUser(pakNo))
  const clearChildModuleStatus = (program?: string) =>
    dispatch(lmsOperations.clearChildModuleStatus(program))
  const clearLessonProgressReportLink = () =>
    dispatch(userOperations.clearLessonProgressReportLink())
  const fetchAndHandleArchetypes = () =>
    dispatch(userOperations.fetchAndHandleArchetypes())
  const fetchAndHandleProductUser = (email: string, id: string) =>
    dispatch(userOperations.fetchAndHandleProductUser(email, id))
  const fetchChildModuleStatus = (
    email: string,
    trackId: number,
    skuCode: string,
  ) => dispatch(lmsOperations.fetchChildModuleStatus(email, trackId, skuCode))
  const fetchLessonProgressReport = (email: string, programCode: string) =>
    dispatch(userOperations.fetchLessonProgressReport(email, programCode))
  const fetchTrainingStatusesCss = (email: string) =>
    dispatch(lmsOperations.fetchTrainingStatusesCss(email))
  const openModal = (modalName: string, modalData?: any) =>
    dispatch(modalActionsCreators.openModal(modalName, modalData))
  const handlePush = (path: string) => dispatch(push(path))
  const handleSearchNetSuiteAssets = (params: { userId: number }) =>
    dispatch(searchNetSuiteAssets(params))
  const setActiveSkuCode = (skuCode: string) =>
    dispatch(lmsOperations.setActiveSkuCode(skuCode))

  const [activeTab, setActiveTab] = useState(Tabs.Sites)
  const [pakNo, setPakNo] = useState('')
  const [isPakValid, setIsPakValid] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [displayHSCompletionReportButton, setDisplayHSCompletionReportButton] =
    useState(false)
  const [displayK8CompletionReportButton, setDisplayK8CompletionReportButton] =
    useState(false)

  useEffect(() => {
    if (!productUser || String(match.params.id) !== String(productUser.id)) {
      fetchAndHandleProductUser(match.params.email, String(match.params.id))
      fetchAndHandleArchetypes()
      fetchTrainingStatusesCss(match.params.email)
      handleSearchNetSuiteAssets({
        userId: parseInt(String(match.params.id), 10),
      })
    }
  }, [])

  useEffect(() => {
    const { assets } = netSuiteAssets

    if (assets == null || assets.length < 1 || productUser === null) {
      setDisplayHSCompletionReportButton(false)
      setDisplayK8CompletionReportButton(false)
      return
    }

    const isHSAdmin = [
      ...assets.filter(
        l =>
          l.product &&
          l.product.programCodes &&
          l.product.programCodes.includes('HSP'),
      ),
    ].some(
      l =>
        l.site &&
        l.site.admins &&
        l.site.admins.some(
          admin =>
            admin.email?.toUpperCase() === productUser.email.toUpperCase(),
        ),
    )
    setDisplayHSCompletionReportButton(isHSAdmin)

    const isK8Admin = [
      ...assets.filter(
        l =>
          l.product &&
          l.product.programCodes &&
          l.product.programCodes.some(c => c === 'MSP' || c === 'K5P'),
      ),
    ].some(
      l =>
        l.site?.admins &&
        l.site.admins.some(
          admin =>
            admin.email?.toUpperCase() === productUser.email.toUpperCase(),
        ),
    )
    setDisplayK8CompletionReportButton(isK8Admin)
  }, [netSuiteAssets, productUser])

  const pakValidation = async (pakNo: string) => {
    const pakIsValid = await validatePak(pakNo)
    setIsPakValid(pakIsValid)
    setIsValidating(false)
  }
  const debouncedPakValidation = _debounce(pakValidation, 300)
  useEffect(() => {
    debouncedPakValidation(pakNo)
  }, [pakNo])

  const backToSearch = () => handlePush('/css-admin/users')
  useEffect(() => {
    if (
      deleteUserResults &&
      match.params.id === deleteUserResults.user.id.toString()
    ) {
      backToSearch()
    }
  }, [match, deleteUserResults])

  const downloadProgressReport = async (url: string) => {
    try {
      if (!url || typeof url !== 'string') {
        return
      }
      const link = document.createElement('a')
      const downloadName = url.split('/').pop()
      link.href = url
      link.download = downloadName || ''
      link.target = '_self'
      link.click()
      clearLessonProgressReportLink()
    } catch (error) {
      // Error downloading progress report - silently continue
    }
  }
  useEffect(() => {
    if (!progressReport.isLoading && progressReport.data) {
      downloadProgressReport(progressReport.data)
    }
  }, [progressReport])

  useEffect(() => {
    return () => {
      clearChildModuleStatus()
    }
  }, [])

  const pakColumns = [
    { title: 'Pak', fieldValue: 'pakNumber' as const },
    { title: 'Grade', fieldValue: 'grade' as const },
    {
      title: 'Registered',
      fieldValue: (pak: Pak) =>
        moment(pak.registrationDate).format('MM-DD-YYYY'),
    },
  ]

  const trainingStatusColumns = [
    { title: 'Name', fieldValue: 'type' as const },
    {
      title: 'Training Path',
      fieldValue: 'current_track_title' as const,
      headerStyle: { width: '2rem' },
      cellClassName: 'text-left',
    },
    {
      title: 'Status',
      fieldValue: (row: TrainingReport) => {
        if (productUser && hasTrainingAccess(productUser.paks, row.skuCode)) {
          return row.status
        } else {
          return ''
        }
      },
      headerStyle: { width: '4rem' },
      cellClassName: 'text-left',
    },
    {
      title: 'Certificate Date',
      fieldValue: (row: TrainingReport) => {
        if (row.doneAt !== null) return moment(row.doneAt).format('MM-DD-YYYY')
      },
      headerStyle: { width: '8rem' },
    },
    {
      title: 'Actions',
      fieldValue: (row: TrainingReport) => {
        return (
          <Fragment>
            {!row.current_track_title && (
              <Button
                color="light"
                disabled={isSavingTraining}
                id={row.skuCode.toLowerCase()}
                onClick={() =>
                  handleEditTrainingModel(row.skuCode.toLowerCase())
                }
                role="button"
                size="sm"
              >
                <i className="fas fa-plus text-success" />
                &nbsp;Add
              </Button>
            )}
            {row.current_track_title && (
              <Button
                color="light"
                disabled={isSavingTraining}
                id={row.skuCode.toLowerCase()}
                onClick={() =>
                  handleEditTrainingModel(row.skuCode.toLowerCase())
                }
                role="button"
                size="sm"
              >
                <i className="fas fa-pencil-alt" />
                &nbsp;Edit
              </Button>
            )}
          </Fragment>
        )
      },
      headerStyle: { width: '8rem' },
      cellClassName: 'text-left',
      sort: false,
      filter: false,
    },
  ]

  const addPak = async () => {
    const success = await addPakToProductUser(pakNo)
    if (success) {
      setPakNo('')
      setIsPakValid(false)
      setIsValidating(false)
    }
  }

  const handlePakChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPakNo(event.target.value)
    setIsPakValid(false)
    setIsValidating(true)
  }

  const hasTrainingAccess = (paks: Pak[], program: string | undefined) => {
    if (!program) return false
    if (netSuiteAssets.isLoading || !netSuiteAssets.assets) {
      return false
    }

    const hasTrainingPAK = paks
      .map(pak => pak.pakNumber.substr(0, 3))
      .includes(program.toUpperCase())

    const hasTrainingLicense = netSuiteAssets.assets
      .reduce((result: string[], lic) => {
        if (lic && lic.product && lic.product.programCodes) {
          return result.concat(lic.product.programCodes)
        }
        return result
      }, [])
      .includes(program.toUpperCase())

    const hasTraining = hasTrainingPAK || hasTrainingLicense

    return hasTraining
  }

  const handleEditTrainingModel = async (skuCode: string) => {
    setActiveSkuCode(skuCode)
    const selectedTraining = cssLmsTrainingTrack.find(
      training => training.program === skuCode,
    )
    if (selectedTraining?.users_current_track) {
      await fetchChildModuleStatus(
        match.params.email,
        selectedTraining.users_current_track.id,
        skuCode,
      )
    }
    if (openModal) {
      openModal('editTrainingModal')
    }
  }

  const handleDownloadLessonProgressReport = async (programCode: string) => {
    if (!productUser || !productUser.email) return
    fetchLessonProgressReport(productUser.email, programCode)
  }

  const trainingTrackCount = () => {
    let count = 0
    cssLmsTrainingTrack.forEach(x => {
      if (
        x.users_current_track ||
        (productUser && hasTrainingAccess(productUser.paks, x.program))
      ) {
        if (x.program === 'bpu') count += 1
        if (x.program === 'cpu') count += 1
      }
    })
    return count
  }

  const userTrainingTracks = () => {
    if (!productUser) return []
    const rows = cssLmsTrainingReport.filter(training =>
      hasTrainingAccess(productUser.paks, training.skuCode),
    )

    return rows
  }

  if (!productUser) return null
  return (
    <ErrorBoundary>
      <Loading isLoading={isSaving || false} />
      <p>
        <Button
          color="primary"
          data-testid="back-to-search-data-test-id"
          onClick={() => backToSearch()}
          outline
          role="button"
        >
          <i className="fas fa-chevron-left" />
          &nbsp; Back to Search
        </Button>
      </p>
      <Loading
        isLoading={isLoading || isDeletingUser || false}
        message={isDeletingUser ? 'Deleting account...' : 'Loading...'}
      />
      <Row style={{ backgroundColor: '#002855' }}>
        <Col>
          <h2
            className="mt-2 text-light"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              User&nbsp;
              <Badge color="light" data-testid="user-id-data-test-id">
                {productUser.id}
              </Badge>
            </div>
            <Button
              color="light"
              onClick={() => openModal && openModal('editProductUserModal')}
              role="button"
              style={{ borderRadius: '2px' }}
            >
              <i className="fas fa-pencil-alt" />
              &nbsp;Edit
            </Button>
          </h2>
        </Col>
      </Row>
      <Row>
        <UserProfile
          displayHSCompletionReportButton={displayHSCompletionReportButton}
          displayK8CompletionReportButton={displayK8CompletionReportButton}
          getLessonProgressReport={handleDownloadLessonProgressReport}
          productUser={productUser}
        />
      </Row>
      <Nav justified tabs>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.Sites}
            data-testid="licenses-tab-data-test-id"
            id="tab-license"
            onClick={() => setActiveTab(Tabs.Sites)}
            onKeyPress={() => setActiveTab(Tabs.Sites)}
            tabIndex={0}
          >
            Sites&nbsp;
            <Badge color="info" data-testid="licenses-badge-data-test-id">
              {productUser.sites.length}
            </Badge>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.Paks}
            data-testid="paks-tab-data-test-id"
            id="tab-paks"
            onClick={() => setActiveTab(Tabs.Paks)}
            onKeyPress={() => setActiveTab(Tabs.Paks)}
            tabIndex={0}
          >
            Paks&nbsp;
            <Badge color="info" data-testid="paks-badge-data-test-id">
              {productUser.paks.length}
            </Badge>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === Tabs.TrainingStatus}
            data-testid="training-tab-data-test-id"
            id="tab-training"
            onClick={() => setActiveTab(Tabs.TrainingStatus)}
            onKeyPress={() => setActiveTab(Tabs.TrainingStatus)}
            tabIndex={0}
          >
            Training&nbsp;
            <Badge color="info" data-testid="training-badge-data-test-id">
              {netSuiteAssets.isLoading ? (
                <i className="spinner fas fa-spinner fa-spin" />
              ) : (
                trainingTrackCount()
              )}
            </Badge>
          </NavLink>
        </NavItem>
      </Nav>
      <br />
      <TabContent activeTab={activeTab}>
        <TabPane tabId={Tabs.Sites}>
          <SitesGrid licenses={productUser.sites} />
        </TabPane>
        <TabPane tabId={Tabs.Paks}>
          <Row className="justify-content-between mb-2">
            <Col className="d-flex align-items-baseline" sm={8}>
              <h4>Paks</h4>
            </Col>
            <Col className="text-right" sm={4}>
              <InputGroup className="justify-content-end">
                <Input
                  className="border-right-0"
                  maxLength={12}
                  onChange={handlePakChange}
                  type="text"
                  value={pakNo}
                />
                <InputGroupAddon addonType="append">
                  <div
                    className="border-top border-bottom d-flex align-items-center justify-content-center"
                    style={{ width: '25px' }}
                  >
                    {isValidating ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : isPakValid ? (
                      <i className="fa fa-check" style={{ color: 'green' }} />
                    ) : pakNo.length > 0 ? (
                      <i className="fa fa-times" style={{ color: 'red' }} />
                    ) : null}
                  </div>
                  <Button
                    color="primary"
                    disabled={pakNo === '' || !isPakValid || isSavingTraining}
                    onClick={addPak}
                    role="button"
                    size="sm"
                  >
                    {isSavingTraining ? (
                      <Fragment>
                        <i className="fa fa-spinner fa-spin" />
                        &nbsp;Saving...
                      </Fragment>
                    ) : (
                      <Fragment>
                        <i className="fa fa-user-plus" />
                        &nbsp;Add Pak
                      </Fragment>
                    )}
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Col>
          </Row>
          <AutoTable
            borderless
            columns={pakColumns}
            filter
            headThemeLight
            rows={productUser.paks}
            sort
          />
        </TabPane>
        <TabPane tabId={Tabs.TrainingStatus}>
          {netSuiteAssets.isLoading && (
            <Loading
              isLoading={netSuiteAssets.isLoading}
              message="Loading Training"
            />
          )}
          {trainingTrackCount() === 0 && (
            <Alert color="warning">No training licenses found</Alert>
          )}
          {trainingTrackCount() > 0 && (
            <Fragment>
              <Row className="mt-1 mb-1">
                <Col className="d-flex align-items-baseline" sm={10}>
                  <h4>Training Status</h4>
                </Col>
              </Row>
              <AutoTable
                borderless
                columns={trainingStatusColumns}
                headThemeLight
                rows={userTrainingTracks() || []}
              />
            </Fragment>
          )}
        </TabPane>
      </TabContent>
    </ErrorBoundary>
  )
}

export default User
